import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion as Motion } from 'framer-motion';
import { SPRING_POP, SPRING_SNAPPY } from '../../constants/motion';
import { getRandomBuddyAsset, SLOT } from '../../lib/buddyManifest';
import audioManager from '../../lib/audioManager';
import s from './BuddyCelebration.module.css';

const HOLD_MS = 800;
// SPRING_SNAPPY settles in ~250–350ms; use the high end as a safety buffer so
// onExitComplete fires only after the exit animation visually finishes.
const EXIT_MS = 360;

export default function BuddyCelebration({ visible, onExitComplete, ambient = false, buddySlug }) {
  // `exiting` only flips inside setTimeout callbacks (never the effect body)
  // — keeps us clear of react-hooks/set-state-in-effect.
  const [exiting, setExiting] = useState(false);

  // The CELEBRATE slot holds 2 poses per buddy. Pick one the moment the buddy
  // becomes visible and hold it stable for the whole in/hold/out cycle — calling
  // the random picker inline on each render would flicker between the two poses
  // mid-animation. This is the "adjust state when a prop changes" pattern
  // (react.dev): a setState during render, so it's lint-clean (no effect) and
  // never resolves an image string per-frame.
  const [src, setSrc] = useState('');
  const [wasVisible, setWasVisible] = useState(false);
  if (visible !== wasVisible) {
    setWasVisible(visible);
    if (visible) setSrc(getRandomBuddyAsset(buddySlug, SLOT.CELEBRATE));
  }

  // Keep the freshest onExitComplete in a ref so the timer callback never
  // closes over a stale parent reference.
  const onExitCompleteRef = useRef(onExitComplete);
  useEffect(() => {
    onExitCompleteRef.current = onExitComplete;
  }, [onExitComplete]);

  useEffect(() => {
    if (ambient) return undefined;
    if (!visible) return undefined;

    // The buddy is bursting in mid-reel — fire its pop (audio-system.md).
    audioManager.playSFX('buddy-celebration-pop');

    const exitTimer = setTimeout(() => {
      setExiting(true);
    }, HOLD_MS);

    const callbackTimer = setTimeout(() => {
      onExitCompleteRef.current?.();
      // Reset for the next cycle. The parent typically flips `visible` to
      // false in response to onExitComplete; either order is fine — both
      // updates batch into the next render where present resolves to false.
      setExiting(false);
    }, HOLD_MS + EXIT_MS);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(callbackTimer);
    };
  }, [visible, ambient]);

  const present = ambient ? visible : (visible && !exiting);

  // The static `.wrap` wrapper owns the absolute positioning + `translateX(-50%)`
  // recenter. The inner Motion.div owns ONLY the framer-motion entrance/hold/exit
  // transform — keeping the recenter off the animated node so framer-motion's
  // inline transform can't clobber it (the bug FP-REVIEW-CELEBRATE-OFFSCREEN-01
  // fixes). AnimatePresence still wraps the conditional Motion.div directly, so
  // exit animations continue to run.
  return (
    <div className={`${s.wrap} ${ambient ? s.wrapAmbient : ''}`} aria-hidden="true">
      <AnimatePresence>
        {present && (
          <Motion.div
            key="buddy"
            className={s.figure}
            initial={{ opacity: 0, y: 80, scale: 0.4, rotate: -8 }}
            animate={{ opacity: 1, y: 0, scale: [0.4, 1.1, 1], rotate: 0 }}
            exit={{ opacity: 0, y: 40, scale: 0.85, transition: SPRING_SNAPPY }}
            transition={{
              ...SPRING_POP,
              // Explicit overshoot for the "pop": peak at 1.1 then settle to 1.
              scale: { type: 'tween', duration: 0.5, times: [0, 0.6, 1], ease: 'easeOut' },
            }}
          >
            <img src={src} alt="" className={s.buddy} />
          </Motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
